import React, { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import { BaseButton, IconButton } from 'components/Button'
import Icon from 'components/Icon'
import Paper from 'components/Paper'
import detachTask from 'helpers/detachTask'

import classes from './Accordion.module.scss'

const noop = () => {}

/** @type {import("./types").default} */
const Accordion = ({
  summary,
  details,
  className,
  disabled = false,
  defaultExpanded = false,
  detailsClassname,
  summaryClassname,
  onExpandClick = noop,
  children,
  withLeftArrow = false,
  paperProps = {},
}) => {
  const [expanded, setExpanded] = useState(disabled || defaultExpanded)
  const [height, setHeight] = useState()

  const ref = useRef(null)

  const toggleAccordion = event => {
    const newExpandedValue = !expanded
    setExpanded(() => newExpandedValue)
    detachTask(onExpandClick, event, { isExpanded: newExpandedValue })
  }

  useEffect(() => {
    if (!ref.current) {
      return
    }

    setHeight(ref.current.scrollHeight)

    let resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        setHeight(entry.target.scrollHeight)
      })
    })

    resizeObserver.observe(ref.current)

    return () => {
      resizeObserver.disconnect()
      resizeObserver = null
    }
  }, [ref])

  const getHeight = () => {
    if (ref.current) {
      return expanded ? { height } : { height: 0 }
    }
    return {}
  }

  return (
    <Paper className={clsx('Accordion', classes.root, className)} {...paperProps}>
      <BaseButton
        component="div"
        role="button"
        disableRipple
        className={clsx(
          'Summary',
          classes.summaryRoot,
          disabled && classes.disabled,
          expanded && 'expanded',
          withLeftArrow && classes.withLeftArrow,
          summaryClassname
        )}
        {...(!disabled ? { onClick: toggleAccordion } : {})}
      >
        <div className={clsx(classes.summaryContent, expanded && classes.expanded)}>{summary}</div>
        {!disabled && (
          <IconButton disableRipple component="div" className={clsx(classes.icon, expanded && classes.expanded)}>
            <Icon icon="chevron-down" data-testid="chevron-down" />
          </IconButton>
        )}
      </BaseButton>

      <div
        className={clsx('Details', classes.details, !expanded ? classes.collapsed : classes.entered)}
        style={getHeight()}
      >
        <div ref={ref} className={clsx(classes.content, detailsClassname)}>
          {details || children}
        </div>
      </div>
    </Paper>
  )
}

export default Accordion
